import { useEffect, useRef, useState } from "react";
import { Manager, type Socket } from "socket.io-client";
import { getDispensaryApiBaseUrl } from "./api-base";

const MAX_EVENTS = 24;

function normalizeWsId(raw: string): string {
  return raw.trim().replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-");
}

export type DispensaryWsFeedItem = { at: string; event: string; preview: string };

/**
 * **§15.2** — Socket.io **`/ws/dispensary/:dispensaryId`** with Clerk JWT in **`auth.token`**.
 * Uses **`Manager` + explicit namespace** (see **`use-transporter-socket`**) so Engine.IO targets the origin only.
 * Uses **`socket.onAny`** for a lightweight activity feed until typed handlers land per event.
 */
export function useDispensarySocket(
  dispensaryId: string | null,
  getToken: () => Promise<string | null>,
  enabled: boolean,
  onNamedEvent?: (eventName: string) => void,
): { connected: boolean; lastError: string | null; feed: DispensaryWsFeedItem[]; socket: Socket | null } {
  const [connected, setConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [feed, setFeed] = useState<DispensaryWsFeedItem[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const onNamedEventRef = useRef(onNamedEvent);
  onNamedEventRef.current = onNamedEvent;

  useEffect(() => {
    const id = dispensaryId ? normalizeWsId(dispensaryId) : null;
    if (!enabled || !id) {
      setConnected(false);
      setSocket(null);
      return;
    }

    const socketRef: { current: Socket | null } = { current: null };
    let cancelled = false;

    void (async () => {
      const token = await getTokenRef.current();
      if (cancelled || !token) {
        if (!cancelled && !token) setLastError("missing_clerk_token");
        return;
      }

      const origin = getDispensaryApiBaseUrl() || globalThis.location?.origin || "";
      const base = origin.replace(/\/$/, "");
      const nsp = `/ws/dispensary/${id}`;

      const manager = new Manager(base, {
        path: "/socket.io/",
        autoConnect: false,
        transports: ["websocket", "polling"],
        reconnectionAttempts: 8,
        reconnectionDelay: 1200,
      });
      const socket = manager.socket(nsp, { auth: { token } });
      if (cancelled) {
        socket.disconnect();
        return;
      }
      socketRef.current = socket;
      setSocket(socket);
      setLastError(null);

      socket.on("connect", () => {
        if (!cancelled) setConnected(true);
      });
      socket.on("disconnect", () => {
        if (!cancelled) setConnected(false);
      });
      socket.on("connect_error", (err: Error) => {
        if (!cancelled) {
          setConnected(false);
          setLastError(err?.message ?? "connect_error");
        }
      });

      socket.onAny((event, ...args) => {
        if (cancelled || event === "connect" || event === "disconnect") return;
        onNamedEventRef.current?.(event);
        let preview = "";
        try {
          preview =
            typeof args[0] === "string"
              ? args[0].slice(0, 200)
              : JSON.stringify(args[0]).slice(0, 200);
        } catch {
          preview = "";
        }
        setFeed((prev) => {
          const next = [
            { at: new Date().toISOString(), event, preview },
            ...prev,
          ].slice(0, MAX_EVENTS);
          return next;
        });
      });

      socket.connect();
    })();

    return () => {
      cancelled = true;
      const s = socketRef.current;
      if (s) {
        s.removeAllListeners();
        s.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      setSocket(null);
    };
  }, [enabled, dispensaryId]);

  return { connected, lastError, feed, socket };
}
